/**
 * Map common color names (English + Arabic) to a Tailwind background class.
 * Returns null when the name is not recognized — callers should render a
 * text-only chip in that case.
 *
 * NOTE: every class string below MUST appear literally so Tailwind's JIT
 * scanner picks it up. Do not refactor to dynamic concatenation.
 */
const SWATCH_CLASSES: Record<string, string> = {
  // English
  red: 'bg-red-500',
  crimson: 'bg-red-600',
  orange: 'bg-orange-500',
  yellow: 'bg-yellow-400',
  gold: 'bg-amber-400',
  green: 'bg-green-500',
  olive: 'bg-lime-700',
  mint: 'bg-emerald-300',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  blue: 'bg-blue-500',
  navy: 'bg-blue-900',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
  violet: 'bg-violet-500',
  pink: 'bg-pink-500',
  magenta: 'bg-fuchsia-500',
  brown: 'bg-amber-800',
  beige: 'bg-amber-100',
  tan: 'bg-amber-200',
  cream: 'bg-amber-50',
  white: 'bg-white',
  black: 'bg-black',
  gray: 'bg-gray-500',
  grey: 'bg-gray-500',
  silver: 'bg-gray-300',
  charcoal: 'bg-gray-700',
  // Arabic
  'أحمر': 'bg-red-500',
  'برتقالي': 'bg-orange-500',
  'أصفر': 'bg-yellow-400',
  'ذهبي': 'bg-amber-400',
  'أخضر': 'bg-green-500',
  'زيتي': 'bg-lime-700',
  'فيروزي': 'bg-teal-500',
  'سماوي': 'bg-cyan-500',
  'أزرق': 'bg-blue-500',
  'كحلي': 'bg-blue-900',
  'نيلي': 'bg-indigo-500',
  'بنفسجي': 'bg-purple-500',
  'وردي': 'bg-pink-500',
  'فوشيا': 'bg-fuchsia-500',
  'بني': 'bg-amber-800',
  'بيج': 'bg-amber-100',
  'كريمي': 'bg-amber-50',
  'أبيض': 'bg-white',
  'أسود': 'bg-black',
  'رمادي': 'bg-gray-500',
  'فضي': 'bg-gray-300',
};

export function getSwatchClass(colorName: string): string | null {
  if (!colorName) return null;
  const normalized = colorName.trim().toLowerCase();
  return SWATCH_CLASSES[normalized] ?? SWATCH_CLASSES[colorName.trim()] ?? null;
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

/** True for `#rgb`, `#rrggbb` or `#rrggbbaa` — the only values we paint with. */
export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

/**
 * Store colour codes (`option_values` rows) keyed by Arabic value name — the
 * key variants use. Codes that aren't a plain hex colour are dropped.
 */
export function toColorHexMap(
  rows: { value_ar: string; color_hex: string | null }[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const row of rows) {
    const hex = row.color_hex?.trim();
    if (hex && isHexColor(hex)) map[row.value_ar] = hex;
  }
  return map;
}

/**
 * Detect whether an option name represents "color" in either locale.
 */
export function isColorOption(nameAr: string | null | undefined, nameEn: string | null | undefined): boolean {
  const ar = (nameAr ?? '').trim();
  const en = (nameEn ?? '').trim().toLowerCase();
  return ar === 'اللون' || en === 'color' || en === 'colour';
}
